import { doc, getDoc, Timestamp, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { UserProfile } from '../types/firestore';
import { TOTP_NUDGE_SNOOZE_MS, TOTP_NUDGE_VERSION } from './totpNudge';

const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(firestore, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data() as UserProfile;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

const requestServiceReverification = async (): Promise<void> => {
  const fn = httpsCallable<undefined, { success: boolean }>(
    functions,
    'requestServiceReverification'
  );
  await fn();
};

const snoozeTotpSecurityNudge = async (uid: string, nowMs = Date.now()): Promise<void> => {
  const userRef = doc(firestore, 'users', uid);
  await updateDoc(userRef, {
    'securityNudges.totpNudgeDismissedAt': serverTimestamp(),
    'securityNudges.totpNudgeSnoozedUntil': Timestamp.fromMillis(nowMs + TOTP_NUDGE_SNOOZE_MS),
    'securityNudges.totpNudgeVersion': TOTP_NUDGE_VERSION,
    updatedAt: serverTimestamp(),
  });
};

interface DeleteOwnAccountRequest {
  confirmationPhrase: string;
}

interface DeletionBlockedArea {
  area: string;
  count: number | null;
  reason: string;
}

interface DeleteOwnAccountResponse {
  success: boolean;
  accountState: 'deleted_auth_marked_firestore';
  deletedNow: string[];
  blocked: DeletionBlockedArea[];
}

const deleteOwnAccount = async (confirmationPhrase: string): Promise<DeleteOwnAccountResponse> => {
  const fn = httpsCallable<DeleteOwnAccountRequest, DeleteOwnAccountResponse>(
    functions,
    'deleteOwnAccount'
  );
  const result = await fn({ confirmationPhrase });
  return result.data;
};

const mapDeleteOwnAccountError = (error: any): string => {
  const code = String(error?.code || '');

  if (code.includes('failed-precondition')) {
    return 'To działanie wymaga świeżego logowania. Zaloguj się ponownie i spróbuj ponownie.';
  }
  if (code.includes('invalid-argument')) {
    return 'Wpisz poprawną frazę potwierdzającą i spróbuj jeszcze raz.';
  }
  if (code.includes('unauthenticated')) {
    return 'Sesja wygasła. Zaloguj się ponownie.';
  }
  if (code.includes('internal')) {
    return 'Nie udało się usunąć konta po stronie serwera. Spróbuj ponownie za chwilę.';
  }

  return 'Nie udało się usunąć konta. Spróbuj ponownie.';
};

export const userService = {
  getUserProfile,
  requestServiceReverification,
  snoozeTotpSecurityNudge,
  deleteOwnAccount,
  mapDeleteOwnAccountError,
};
